import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../common/SEO';
import Layout from '../common/Layout';
import LegalPage from '../components/ui/LegalPage';
import DataTable from '../components/ui/DataTable';
import Notice from '../components/ui/Notice';
import DlFacts from '../components/ui/DlFacts';
import { entity } from '../content/en';

const Privacy = () => {
  const { t } = useTranslation('cy');
  const meta = t('privacy.meta', { returnObjects: true });

  const sections = [
    { id: 'privacy-collection', title: t('privacy.collection.h2') },
    { id: 'privacy-use', title: t('privacy.use.h2') },
    { id: 'privacy-disclosure', title: t('privacy.disclosure.h2') },
    { id: 'privacy-retention-security', title: t('privacy.retention.h2') },
    { id: 'privacy-rights', title: t('privacy.rights.h2') },
    { id: 'privacy-cookies-links', title: t('privacy.cookies.h2') },
    { id: 'privacy-contact', title: t('privacy.contact.h2') },
  ];

  return (
    <Layout>
      <SEO title={meta.title} description={meta.description} path="/privacy" />

      <LegalPage
        id="privacy-header"
        eyebrow={t('privacy.header.eyebrow')}
        title={t('privacy.header.h1')}
        metaBlock={
          <DlFacts
            rows={[
              { label: t('ui.labels.controller'), value: entity.name },
              { label: t('ui.labels.updated'), value: t('privacy.header.updated') },
            ]}
          />
        }
        summary={t('privacy.header.summary')}
        sections={sections}
      >
        <Notice variant="compliance" style={{ marginBottom: 24 }}>{t('privacy.header.notice')}</Notice>
        <p style={{ fontSize: 'var(--cy-size-body)', lineHeight: 1.75, color: 'var(--cy-color-text-secondary)' }}>
          {t('privacy.header.scope')}
        </p>

        <section id="privacy-collection">
          <h2>{t('privacy.collection.h2')}</h2>
          <DataTable
            columns={[
              { key: 'scenario', label: t('ui.cols.scenario') },
              { key: 'data', label: t('ui.cols.dataCategories') },
              { key: 'source', label: t('ui.cols.source') },
              { key: 'required', label: t('ui.cols.required') },
            ]}
            rows={t('privacy.collection.rows', { returnObjects: true })}
          />
          <p style={{ marginTop: 24 }}>{t('privacy.collection.sensitive')}</p>
        </section>

        <section id="privacy-use">
          <h2>{t('privacy.use.h2')}</h2>
          <DataTable
            columns={[
              { key: 'purpose', label: t('ui.cols.purpose') },
              { key: 'data', label: t('ui.cols.data') },
              { key: 'basis', label: t('ui.cols.basis') },
              { key: 'consequence', label: t('ui.cols.consequence') },
            ]}
            rows={t('privacy.use.rows', { returnObjects: true })}
          />
          <p style={{ marginTop: 24 }}>{t('privacy.use.marketing')}</p>
        </section>

        <section id="privacy-disclosure">
          <h2>{t('privacy.disclosure.h2')}</h2>
          <DataTable
            columns={[
              { key: 'recipient', label: t('ui.cols.recipient') },
              { key: 'purpose', label: t('ui.cols.purpose') },
              { key: 'data', label: t('ui.cols.data') },
              { key: 'location', label: t('ui.cols.location') },
              { key: 'safeguard', label: t('ui.cols.safeguard') },
            ]}
            rows={t('privacy.disclosure.rows', { returnObjects: true })}
          />
          <Notice variant="inline" style={{ marginTop: 24 }}>{t('privacy.disclosure.note')}</Notice>
        </section>

        <section id="privacy-retention-security">
          <h2>{t('privacy.retention.h2')}</h2>
          <DataTable
            columns={[
              { key: 'type', label: t('ui.cols.recordType') },
              { key: 'term', label: t('ui.cols.term') },
            ]}
            rows={t('privacy.retention.rows', { returnObjects: true })}
          />
          <p style={{ marginTop: 24 }}>{t('privacy.retention.security')}</p>
          <p>{t('privacy.retention.incident')}</p>
        </section>

        <section id="privacy-rights">
          <h2>{t('privacy.rights.h2')}</h2>
          <DataTable
            columns={[
              { key: 'right', label: t('ui.cols.right') },
              { key: 'how', label: t('ui.cols.how') },
              { key: 'limit', label: t('ui.cols.limit') },
            ]}
            rows={t('privacy.rights.rows', { returnObjects: true })}
          />
        </section>

        <section id="privacy-cookies-links">
          <h2>{t('privacy.cookies.h2')}</h2>
          <p>{t('privacy.cookies.note')}</p>
          <p>{t('privacy.cookies.external')}</p>
        </section>

        <section id="privacy-contact">
          <h2>{t('privacy.contact.h2')}</h2>
          <p>{t('privacy.contact.enquiries')}</p>
          <p>{t('privacy.contact.complaints')}</p>
          <p>{t('privacy.contact.updates')}</p>
          <p style={{ marginTop: 24 }}>
            <Link className="cy-link" to="/contact">{t('privacy.contact.cta')}</Link>
          </p>
        </section>
      </LegalPage>
    </Layout>
  );
};

export default Privacy;
